import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { 
  subscribeToMarketDepth, 
  subscribeToMarketTrades,
  subscribeToMarketDetail,
  unsubscribeFromMarketDepth,
  unsubscribeFromMarketTrades,
  unsubscribeFromMarketDetail
} from '../socket/htx/eventSubscriber';
import { htxWebSocket } from '../socket/htx/wsClient';

/**
 * Custom hook to manage depth, trade, and detail subscriptions for the selected market
 * @param {string} selectedMarket - The currently selected market instanceId
 */
export const useMarketDepthSubscription = (selectedMarket) => {
  const markets = useSelector((state) => state?.crypto?.markets?.markets);
  const previousMarketRef = useRef(null);

  useEffect(() => {
    let marketSymbol;
    let marketObj={};
    if (!selectedMarket ) return;
    if(!markets?.length){
      marketSymbol = selectedMarket.toLowerCase().replace(/[\/\-]/g, '');
    }
    else{
    // Find the market object by instanceId
    marketObj = markets.find(market => market.pairName.toLowerCase().replace(/[\/\-]/g, '') === selectedMarket.toLowerCase().replace(/[\/\-]/g, ''));
    if (!marketObj?.pairName) return;

    // Convert pairName to the format expected by HTX (lowercase, no separators)
     marketSymbol = marketObj?.pairName.toLowerCase().replace(/[\/\-]/g, '');
    }
    const previousMarket = previousMarketRef.current;
    const isInitialLoad = !previousMarket;

    // If this is a different market than before, unsubscribe from the previous one
    if (previousMarket && previousMarket !== marketSymbol) {
      console.log(`Unsubscribing from depth, trades, and detail for previous market: ${previousMarket}`);
      unsubscribeFromMarketDepth(previousMarket);
      unsubscribeFromMarketTrades(previousMarket);
      unsubscribeFromMarketDetail(previousMarket);
    }

    // Subscribe to depth, trades, and detail for the new market with initial data fetch
    if (isInitialLoad) {
      console.log(`Initial market selection: ${marketSymbol} (${marketObj.pairName}) - subscribing to all events`);
    } else {
      console.log(`Market changed to: ${marketSymbol} (${marketObj.pairName}) - subscribing to all events`);
    }
    
    subscribeToMarketDepth(marketSymbol, htxWebSocket.updateState);
    subscribeToMarketTrades(marketSymbol);
    subscribeToMarketDetail(marketSymbol);

    // Update the ref to track the current market
    previousMarketRef.current = marketSymbol;

    // Cleanup function - unsubscribe when component unmounts
    return () => {
      if (marketSymbol) {
        console.log(`Cleaning up depth, trade, and detail subscriptions for market: ${marketSymbol}`);
        unsubscribeFromMarketDepth(marketSymbol);
        unsubscribeFromMarketTrades(marketSymbol);
        unsubscribeFromMarketDetail(marketSymbol);
        previousMarketRef.current = null;
      }
    };
  }, [selectedMarket, markets]);
};

export default useMarketDepthSubscription;
